import React, { useEffect, useState } from 'react';

/**
 * Amazon's MCF-with-Prime web components. The card goes on the product page. The message goes on the bag and
 * checkout.
 *
 * - Amazon renders into a closed shadow root. The page cannot read the content, only the element height.
 * - An empty render is normal. Amazon returns nothing when it has no promise for a SKU. Do not report or retry it.
 * - Amazon serves only the origins on the domain allowlist in Seller Central. Each origin that shows an element must
 *   be on that list.
 */

type AmazonDeliveryElement = 'amazon-delivery-message' | 'amazon-delivery-card';

const loads = new Map<AmazonDeliveryElement, Promise<boolean>>();

/**
 * Resolves true when the element is defined, or false when its script fails. Adds the script only when the page
 * does not have it already, for example from a theme.
 */
function loadAmazonDeliveryElement(element: AmazonDeliveryElement, targetId: string): Promise<boolean> {
  if (typeof window === 'undefined' || !window.customElements) return Promise.resolve(false);
  if (window.customElements.get(element)) return Promise.resolve(true);

  let load = loads.get(element);
  if (!load) {
    load = new Promise<boolean>((resolve) => {
      window.customElements.whenDefined(element).then(() => resolve(true));
      if (document.querySelector(`script[src*="/elements/${element}/"]`)) return;

      const script = document.createElement('script');
      script.src = `https://cdn.elements.amazon/elements/${element}/v1.0/web-component.js`;
      script.type = 'module';
      script.async = true;
      // The bundle reads its target from its own script tag.
      script.setAttribute('data-target-id', targetId);
      // A blocked script is the same outcome as an empty render, so it is not an error.
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
    loads.set(element, load);
  }
  return load;
}

/** True once the element is defined. Before that, and after a failed script, the component renders nothing. */
function useAmazonDeliveryElement(element: AmazonDeliveryElement, targetId: string): boolean {
  const [defined, setDefined] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadAmazonDeliveryElement(element, targetId).then((isDefined) => {
      if (!cancelled) setDefined(isDefined);
    });
    return () => {
      cancelled = true;
    };
  }, [element, targetId]);

  return defined;
}

/** Amazon's product-page Prime card for one SKU: badge, delivery estimate and "Learn more". */
export const AmazonDeliveryCard = ({
  targetId,
  amazonSku,
  fontFamily,
}: {
  targetId: string;
  amazonSku: string;
  /** The page's font. Amazon uses its own when this is absent. */
  fontFamily?: string;
}) => {
  const defined = useAmazonDeliveryElement('amazon-delivery-card', targetId);
  if (!defined) return null;

  return (
    <amazon-delivery-card
      product-amazon-sku={amazonSku}
      show-prime-upgrade="true"
      color-mode="light"
      font-family={fontFamily}
    />
  );
};

interface AmazonDeliveryItem {
  amazonSku: string;
  quantity: number;
}

/**
 * One element for the given lines. Amazon combines the items into one promise. `none` shows the Prime badge
 * alone.
 */
export const AmazonDeliveryMessage = ({
  targetId,
  items,
  messageType = 'delivery-estimate',
  context,
}: {
  targetId: string;
  items: AmazonDeliveryItem[];
  messageType?: 'none' | 'delivery-estimate';
  context: 'cart' | 'checkout';
}) => {
  const defined = useAmazonDeliveryElement('amazon-delivery-message', targetId);
  if (!defined || !items.length) return null;

  return (
    <amazon-delivery-message
      target-id={targetId}
      message-type={messageType}
      context={context}
      // MCF with Prime requires this attribute. It makes the promise a Prime promise.
      show-prime-upgrade="true"
    >
      {items.map((item, index) => (
        <amazon-delivery-item
          // A SKU can occur in more than one line, so the key includes the index.
          key={`${item.amazonSku}-${index}`}
          product-amazon-sku={item.amazonSku}
          quantity={String(item.quantity)}
        />
      ))}
    </amazon-delivery-message>
  );
};

/**
 * TSX types for the custom elements. The elements read kebab-case attributes, not properties. React 19 does not
 * read the global `JSX` namespace, so the declaration goes on React's own namespace.
 */
declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace -- React declares JSX as a namespace, so an augmentation must too.
  namespace JSX {
    interface IntrinsicElements {
      'amazon-delivery-message': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        'target-id'?: string;
        'message-type'?: 'none' | 'options-available' | 'delivery-estimate' | 'delivery-estimate-with-returns';
        context?: string;
        'show-prime-upgrade'?: string;
        'font-size-base'?: string;
        'color-mode'?: 'light' | 'dark';
        'text-color'?: string;
        'content-alignment'?: 'left' | 'center' | 'right';
      };
      'amazon-delivery-item': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        'product-amazon-sku'?: string;
        quantity?: string;
      };
      'amazon-delivery-card': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        'product-amazon-sku'?: string;
        'show-prime-upgrade'?: string;
        'color-mode'?: 'light' | 'dark';
        'font-family'?: string;
        'font-size-base'?: string;
      };
    }
  }
}
